var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ShareLocationIcon from '@mui/icons-material/ShareLocation';
import EditNoteIcon from '@mui/icons-material/EditNote';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { Button, Skeleton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import DialogYesNo from './elements/DialodYesNo';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import ApiIcon from '@mui/icons-material/Api';
import { forwardRef } from 'react';
import useToken from '../services/token';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import GroupsIcon from '@mui/icons-material/Groups';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useSnackbar } from 'notistack';
import { useOnboarding } from './onboarding/OnboardingProvider';
const drawerWidth = 240;
function ElevationScroll(props) {
    const { children, window } = props;
    const trigger = useScrollTrigger({
        disableHysteresis: true,
        threshold: 0,
        target: window ? window() : undefined,
    });
    return React.cloneElement(children, {
        elevation: trigger ? 4 : 0,
    });
}
const openedMixin = (theme) => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});
const closedMixin = (theme) => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});
const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    minHeight: '48px !important',
    height: '48px',
}));
const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => (Object.assign({ zIndex: theme.zIndex.drawer + 1, transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }) }, (open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
}))));
const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => (Object.assign(Object.assign({ width: drawerWidth, flexShrink: 0, whiteSpace: 'nowrap', boxSizing: 'border-box' }, (open && Object.assign(Object.assign({}, openedMixin(theme)), { '& .MuiDrawer-paper': openedMixin(theme) }))), (!open && Object.assign(Object.assign({}, closedMixin(theme)), { '& .MuiDrawer-paper': closedMixin(theme) })))));
// Пункты меню по ТЗ 3.0 — три основных раздела платформы.
const MAIN_NAV = [
    { key: 'fields', label: 'Опытные участки', path: '/', icon: ShareLocationIcon },
    { key: 'data-entry', label: 'Внесение данных', path: '/data-entry', icon: EditNoteIcon },
    { key: 'knowledge-base', label: 'База знаний', path: '/knowledge-base', icon: MenuBookIcon },
];
const TEAM_ROLES = ['admin', 'Главный агроном'];
const MiniDrawer = forwardRef((props, ref) => {
    const theme = useTheme();
    const [open, setOpen] = React.useState(false);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [username, setUserName] = React.useState('');
    const { token, parseJwt } = useToken();
    const { openHelp } = useOnboarding();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const role = React.useMemo(() => {
        var _a;
        if (!token)
            return null;
        const payload = parseJwt(token);
        return (_a = payload === null || payload === void 0 ? void 0 : payload.role) !== null && _a !== void 0 ? _a : null;
    }, [token]);
    const canSeeAdmin = role === 'admin';
    const canSeeTeam = role !== null && TEAM_ROLES.includes(role);
    const canSeeDocs = role === 'admin';
    // Раньше здесь везде сравнивалось с захардкоженной строкой 'nvdi' —
    // подсветка активного пункта фактически не работала. Теперь сравниваем
    // с типом конкретного пункта.
    const isActive = (type) => props.type === type;
    const getIconColor = (type) => isActive(type)
        ? theme.palette.primary.main
        : theme.palette.text.secondary;
    // Рамка вместо заливки — по ТЗ: "Активный пункт выделяется рамкой".
    const itemSx = (type) => ({
        minHeight: 48,
        justifyContent: open ? 'initial' : 'center',
        px: 2.5,
        mx: 0.5,
        borderRadius: 1,
        border: '1.5px solid',
        borderColor: isActive(type) ? theme.palette.primary.main : 'transparent',
    });
    React.useEffect(() => {
        if (token) {
            const payload = parseJwt(token);
            if (payload) {
                setUserName(payload.username);
            }
        }
    }, []);
    const handleDrawerOpen = () => {
        setOpen(true);
    };
    const handleDrawerClose = () => {
        setOpen(false);
    };
    const openDocs = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!token)
            return;
        try {
            const response = yield fetch('/auth/docs-session', {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                credentials: 'same-origin',
            });
            if (!response.ok)
                throw new Error('Docs session failed');
            window.location.href = '/docs';
        }
        catch (_a) {
            enqueueSnackbar('Не удалось открыть документацию API', { variant: 'error' });
        }
    });
    const logout = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield fetch('/auth/docs-session', { method: 'DELETE', credentials: 'same-origin' });
        }
        catch (_b) {
            // A failed network request must not prevent the local sign-out.
        }
        window.localStorage.removeItem("token");
        window.location.href = "/login";
    });
    return (React.createElement(Box, { sx: { display: 'flex', height: '100%' } },
        React.createElement(ElevationScroll, { window: () => { return window; } },
            React.createElement(AppBar, { position: "fixed", open: open },
                React.createElement(Toolbar, { "data-onboarding-anchor": "top-bar", sx: { minHeight: '48px !important' } },
                    React.createElement(IconButton, { color: "inherit", "aria-label": "open drawer", onClick: handleDrawerOpen, edge: "start", sx: Object.assign({ marginRight: 5 }, (open && { display: 'none' })) },
                        React.createElement(MenuIcon, null)),
                    React.createElement(Typography, { variant: "h6", noWrap: true, component: "div", sx: { flexGrow: 1 } }, "Precision Agriculture Framework"),
                    username == ''
                        ?
                            (React.createElement(Skeleton, { width: 60 }))
                        :
                            (React.createElement(Typography, { variant: "button", display: "block", gutterBottom: true, sx: { marginTop: '6px' } }, username)),
                    React.createElement(Button, { color: "inherit", startIcon: React.createElement(HelpOutlineIcon, null), onClick: openHelp, "data-onboarding-anchor": "help-button", sx: { ml: 1 } }, "\u041F\u043E\u043C\u043E\u0449\u044C"),
                    React.createElement(Button, { color: "inherit", startIcon: React.createElement(PersonIcon, null), onClick: () => navigate('/profile'), sx: { ml: 2 } }, "\u041F\u0440\u043E\u0444\u0438\u043B\u044C"),
                    React.createElement(Divider, { orientation: "vertical", variant: "middle", flexItem: true, sx: {
                            marginLeft: '16px',
                            marginRight: '8px',
                            height: '25px',
                            marginTop: '19px',
                            borderColor: 'white'
                        } }),
                    React.createElement(Button, { color: "inherit", onClick: () => setDialogOpen(true) }, "\u0412\u044B\u0445\u043E\u0434")))),
        React.createElement(Drawer, { variant: "permanent", open: open },
            React.createElement(DrawerHeader, null,
                React.createElement(IconButton, { onClick: handleDrawerClose }, theme.direction === 'rtl' ? React.createElement(ChevronRightIcon, null) : React.createElement(ChevronLeftIcon, null))),
            React.createElement(Divider, null),
            React.createElement(List, null,
                MAIN_NAV.filter(({ key }) => role !== 'user' || key === 'knowledge-base').map(({ key, label, path, icon: Icon }) => (React.createElement(ListItem, { key: key, disablePadding: true, sx: { display: 'block' }, onClick: () => navigate(path) },
                    React.createElement(ListItemButton, { "data-onboarding-anchor": `nav-${key}`, sx: itemSx(key) },
                        React.createElement(ListItemIcon, { sx: {
                                minWidth: 0,
                                mr: open ? 3 : 'auto',
                                justifyContent: 'center',
                            } },
                            React.createElement(Icon, { sx: { color: getIconColor(key) } })),
                        React.createElement(ListItemText, { primary: label, sx: { opacity: open ? 1 : 0 } }))))),
                canSeeTeam && (React.createElement(ListItem, { key: "team", disablePadding: true, sx: { display: 'block' }, onClick: () => navigate('/team') },
                    React.createElement(ListItemButton, { sx: itemSx('team') },
                        React.createElement(ListItemIcon, { sx: { minWidth: 0, mr: open ? 3 : 'auto', justifyContent: 'center' } },
                            React.createElement(GroupsIcon, { sx: { color: getIconColor('team') } })),
                        React.createElement(ListItemText, { primary: "\u041A\u043E\u043C\u0430\u043D\u0434\u0430", sx: { opacity: open ? 1 : 0 } })))),
                canSeeAdmin && (React.createElement(ListItem, { key: "admin", disablePadding: true, sx: { display: 'block' }, onClick: () => navigate('/admin') },
                    React.createElement(ListItemButton, { sx: itemSx('admin') },
                        React.createElement(ListItemIcon, { sx: { minWidth: 0, mr: open ? 3 : 'auto', justifyContent: 'center' } },
                            React.createElement(AdminPanelSettingsIcon, { sx: { color: getIconColor('admin') } })),
                        React.createElement(ListItemText, { primary: "\u0410\u0434\u043C\u0438\u043D\u0438\u0441\u0442\u0440\u0438\u0440\u043E\u0432\u0430\u043D\u0438\u0435", sx: { opacity: open ? 1 : 0 } }))))),
            React.createElement(List, null,
                canSeeDocs && (React.createElement(ListItem, { key: 'api', disablePadding: true, sx: { display: 'block' }, onClick: openDocs },
                    React.createElement(ListItemButton, { sx: itemSx('api') },
                        React.createElement(ListItemIcon, { sx: { minWidth: 0, mr: open ? 3 : 'auto', justifyContent: 'center' } },
                            React.createElement(ApiIcon, { sx: { color: getIconColor('api') } })),
                        React.createElement(ListItemText, { primary: 'Api Сервис', sx: { opacity: open ? 1 : 0 } })))),
                React.createElement(ListItem, { key: 'profile', disablePadding: true, sx: { display: 'block' }, onClick: () => navigate('/profile') },
                    React.createElement(ListItemButton, { sx: itemSx('profile') },
                        React.createElement(ListItemIcon, { sx: { minWidth: 0, mr: open ? 3 : 'auto', justifyContent: 'center' } },
                            React.createElement(PersonIcon, { sx: { color: getIconColor('profile') } })),
                        React.createElement(ListItemText, { primary: 'Профиль', sx: { opacity: open ? 1 : 0 } }))))),
        React.createElement(Box, { component: "main", sx: {
                flexGrow: 1,
                p: 0, flex: '1 1 auto',
                minWidth: 0,
                minHeight: 0,
                position: 'relative',
                display: 'flex',
                flexDirection: 'column'
            } },
            React.createElement(DrawerHeader, null),
            props.children),
        React.createElement(DialogYesNo, { title: '\u0412\u044B\u0445\u043E\u0434', question: '\u0412\u044B \u0445\u043E\u0442\u0438\u0442\u0435 \u0432\u044B\u0439\u0442\u0438 \u0438\u0437 \u0441\u0438\u0441\u0442\u0435\u043C\u044B?', yes: logout, no: (x) => setDialogOpen(x), open: dialogOpen })));
});
export default MiniDrawer;
